"use client";
import { ChangeEvent, useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import "../Calendar/Calendar.css";
import ModalIconTitle from "../ModalIconTitle";
import { MdOutlineCalendarMonth } from "react-icons/md";
import { AxiosResponse } from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import TextInput from "./Input/TextInput";
import { useModalWidthClass } from "../../hooks/useModalWidth";
import { CreateCalendarRequest,UpdateCalendarRequest } from "../../class/calendar.schema";
import { BackendApi } from "../../utils/BackendApi";
import { ApiRoute } from "../../utils/ApiRoute";
import { cal_calendar } from "../../class/prisma/db";
import type toast from "react-hot-toast";
import { SystemMsg } from "../../utils/SystemMsg";
import ColorPicker from "./Input/ColorPicker";
import { CalendarViewObject } from "../../class/calendar.schema";
import { useI18n } from "../../i18n";

type CalendarModalProp = {
  toast: typeof toast;
  mode: string;
  show: boolean;
  onSaveBtnClick: (success: boolean, data: CalendarViewObject) => void;
  onClose: () => void;
  data: any;
};

type CalendarModalValidationResult = {
  name_exist: boolean;
  name_empty: boolean;
  color: boolean;
};

export default function CalendarModal({
  toast,
  mode,
  show,
  onSaveBtnClick,
  onClose,
  data,
}: CalendarModalProp) {
  const calendars = useSelector((state: RootState) => state.cache.calendars);
  const defaultColor = "#000000";
  const [id, setId] = useState(-1);
  const [name, setName] = useState("");
  const [color, setColor] = useState(defaultColor);
  const [defaultShow, setDefaultShow] = useState(true);

  const [validationResult, setValidationResult] =
    useState<CalendarModalValidationResult>();

  const isAddMode = mode === "add";
  const isEditMode = mode === "edit";
  const { t } = useI18n();
  const modalTitle = mode === "add" ? t("calendar.add") : t("calendar.edit");

  const dialogClassName = useModalWidthClass();

  useEffect(() => {
    if (isEditMode && data) {
      setId(data.id ?? -1);
      setName(data.name ?? "");
      setColor(data.color ?? defaultColor);
      setDefaultShow(data.defaultShow ?? true);
    }
  }, [mode, data]);

  const saveCalendarClick = async () => {
    if (!validateForm()) {
      return;
    }
    const requestBody: any = {
      name,
      color,
    };

    const callbackData = {
      id: id,
      name: name,
      color: color,
      defaultShow: defaultShow,
    };

    const succFunc = (res: AxiosResponse) => {
      const success = res.status === 200;
      if (success) {
        toast.success(
          isAddMode
            ? SystemMsg.Calendar.createSuccess(name)
            : SystemMsg.Calendar.updateSuccess(name),
        );
      }
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(success, callbackData as CalendarViewObject);
      }
    };
    const errFunc = (res: AxiosResponse | string) => {
      toast.error(
        isAddMode
          ? SystemMsg.Calendar.createFailed
          : SystemMsg.Calendar.updateFailed(name),
      );
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(false, callbackData as CalendarViewObject);
      }
    };
    if (isEditMode) {
      requestBody[`defaultShow`] = defaultShow;
      await BackendApi.put(
        `${ApiRoute.calendar}/${id}`,
        requestBody as UpdateCalendarRequest,
        succFunc,
        errFunc,
      );
    }
    if (isAddMode) {
      await BackendApi.post(
        ApiRoute.calendar,
        requestBody as CreateCalendarRequest,
        succFunc,
        errFunc,
      );
    }
  };

  const onHide = () => {
    setId(-1);
    setName("");
    setColor(defaultColor);
    setValidationResult(undefined);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const validateForm = () => {
    const name_find = (calendars as unknown as cal_calendar[]).find(
      (x: cal_calendar) => x.name === name,
    );
    const name_exist = isAddMode && name_find !== undefined;

    const name_empty = !name || name.toString().length === 0;
    const name_valid = !name_empty && !name_exist;
    const color_valid = !color || color.toString().length === 0 ? false : true;

    const result: CalendarModalValidationResult = {
      name_exist: name_exist,
      name_empty: name_empty,
      color: color_valid,
    };

    if (!name_valid) {
      if (name_exist) {
        toast.error(SystemMsg.Calendar.calendarNameExists(name));
      } else {
        toast.error(SystemMsg.Calendar.calendarNameInvalid(name));
      }
    }

    if (!color_valid) {
      toast.error(SystemMsg.Calendar.colorInvalid);
    }

    setValidationResult(result);
    const valid = name_valid && color_valid;
    return valid;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      dialogClassName={dialogClassName}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <ModalIconTitle
            title={modalTitle}
            iconElement={<MdOutlineCalendarMonth />}
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <TextInput
            label={t("calendar.name")}
            value={name}
            md={8}
            placeholder={t("calendar.namePlaceholder")}
            setValueFunc={(val: string | number) => {
              setName(val as string);
            }}
            invalid={
              validationResult !== undefined &&
              validationResult?.name_empty === true
            }
          />
          <Form.Group as={Col} controlId="eventModal.color">
            <Form.Label>{t("calendar.color")}</Form.Label>
            <ColorPicker
              value={color}
              setValueFunc={(colorVal: string) => {
                setColor(colorVal);
              }}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="eventModal.defaultShow">
            <Form.Check
              reverse
              type="switch"
              label={t("calendar.defaultShow")}
              className="d-inline-block"
              checked={defaultShow}
              style={{ fontSize: "1.05rem" }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setDefaultShow(event.target.checked);
              }}
            />
          </Form.Group>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={saveCalendarClick}>
          {t("common.saveChanges")}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          {t("common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
